import { renderToStaticMarkup } from "react-dom/server";

import { DEFAULT_SINGLE_STYLE_FILL_COLOR } from "@/constants/dataset";
import type {
  CustomCategoryValue,
  CustomRangeValue,
  SingleStyleValue
} from "@/types/dataset";

import { Icon } from "equator-design-system";

export const MAX_RECENT_MARKER_IMAGES_ICONS = 5;
const STORAGE_KEY_MARKER_ICONS = "custom-legend-recent-marker-icons";
const STORAGE_KEY_MARKER_IMAGES = "custom-legend-recent-marker-images";

// Persisted get marker icon from localStorage
export const getRecentMarkerIcons = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MARKER_ICONS);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item): item is string => typeof item === "string")
      .slice(0, MAX_RECENT_MARKER_IMAGES_ICONS);
  } catch {
    // Corrupt/unavailable storage — treat as empty rather than throwing.
    return [];
  }
};

// Adding marker icon to localStorage
export const addRecentMarkerIcon = (iconId: string): string[] => {
  const existing = getRecentMarkerIcons().filter((item) => item !== iconId);
  const next = [iconId, ...existing].slice(0, MAX_RECENT_MARKER_IMAGES_ICONS);
  try {
    localStorage.setItem(STORAGE_KEY_MARKER_ICONS, JSON.stringify(next));
  } catch {
    // Storage full/unavailable — the in-memory list is still returned.
  }
  return next;
};

export interface RecentMarkerImage {
  url: string;
  name: string;
  assetId: string | null;
}

export const getRecentMarkerImages = (): RecentMarkerImage[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MARKER_IMAGES);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (item): item is RecentMarkerImage =>
          !!item && typeof item.url === "string"
      )
      .slice(0, MAX_RECENT_MARKER_IMAGES_ICONS);
  } catch {
    // Corrupt/unavailable storage — treat as empty rather than throwing.
    return [];
  }
};

/** Prepends an image to the recent list (deduped, capped) and returns it. */
export const addRecentMarkerImage = (
  image: RecentMarkerImage
): RecentMarkerImage[] => {
  const existing = getRecentMarkerImages().filter(
    (item) => (image.assetId ?? image.url) !== (item.assetId ?? item.url)
  );
  const next = [image, ...existing].slice(0, MAX_RECENT_MARKER_IMAGES_ICONS);
  try {
    localStorage.setItem(STORAGE_KEY_MARKER_IMAGES, JSON.stringify(next));
  } catch {
    // Storage full/unavailable — the in-memory list is still returned.
  }
  return next;
};

const STORAGE_KEY_MARKER_IMAGE_CACHE = "custom-legend-marker-image-cache";
const MAX_CACHED_MARKER_IMAGES = 50;

// Persistent `assetId -> base64 data URL` cache. Populated on upload so a saved
export const getCachedMarkerImage = (
  assetId: string | null | undefined
): string | null => {
  if (!assetId) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MARKER_IMAGE_CACHE);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, string>;
    const url = parsed?.[assetId];
    return isLocalImageUrl(url) ? url : null;
  } catch {
    return null;
  }
};

/** Stores an image's base64 against its asset id (capped, oldest evicted). */
export const cacheMarkerImage = (
  assetId: string | null | undefined,
  dataUrl: string
): void => {
  if (!assetId || !isLocalImageUrl(dataUrl)) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY_MARKER_IMAGE_CACHE);
    const parsed = raw ? (JSON.parse(raw) as Record<string, string>) : {};
    // Re-insert last so it counts as most-recent; then cap from the front.
    delete parsed[assetId];
    parsed[assetId] = dataUrl;
    const keys = Object.keys(parsed);
    const capped =
      keys.length > MAX_CACHED_MARKER_IMAGES
        ? Object.fromEntries(
            keys
              .slice(keys.length - MAX_CACHED_MARKER_IMAGES)
              .map((k) => [k, parsed[k]])
          )
        : parsed;
    localStorage.setItem(
      STORAGE_KEY_MARKER_IMAGE_CACHE,
      JSON.stringify(capped)
    );
  } catch {
    // Storage full/unavailable — the marker just falls back to a fetch/dot.
  }
};

/**
 * Stamps uploaded asset ids onto recent entries (matched by data URL) so a
 * re-apply from Recent doesn't upload the same image again, even across reloads.
 * Also seeds the {@link getCachedMarkerImage} cache so the freshly-uploaded
 * image renders on reopen without a cross-origin fetch.
 */
export const applyRecentMarkerImageAssetIds = (
  assetIdByImageUrl: ReadonlyMap<string, string>
): void => {
  if (!assetIdByImageUrl.size) return;
  assetIdByImageUrl.forEach((assetId, imageUrl) =>
    cacheMarkerImage(assetId, imageUrl)
  );
  let changed = false;
  const next = getRecentMarkerImages().map((item) => {
    const id = item.assetId ? undefined : assetIdByImageUrl.get(item.url);
    if (!id) return item;
    changed = true;
    return { ...item, assetId: id };
  });
  if (!changed) return;
  try {
    localStorage.setItem(STORAGE_KEY_MARKER_IMAGES, JSON.stringify(next));
  } catch {
    // Storage full/unavailable — the recent list just stays un-stamped.
  }
};

/** Converts a `data:` URL to a Blob for upload (returns null on failure). */
export const dataUrlToBlob = (dataUrl: string): Blob | null => {
  try {
    const [header, base64] = dataUrl.split(",");
    if (!base64) return null;
    const mimeMatch = /data:(.*?);base64/.exec(header);
    const mime = mimeMatch?.[1] ?? "application/octet-stream";
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
    return new Blob([bytes], { type: mime });
  } catch {
    return null;
  }
};

/** Whether a URL is a not-yet-uploaded local image (a `data:` URL). */
export const isLocalImageUrl = (url: string | null | undefined): boolean =>
  typeof url === "string" && url.startsWith("data:");

/** Lowercased file extension of a URL's path, ignoring the query/hash. */
const urlPathExtension = (url: string): string => {
  const path = url.split(/[?#]/)[0];
  const dot = path.lastIndexOf(".");
  return dot >= 0 ? path.slice(dot + 1).toLowerCase() : "";
};

/** Marker image types we embed, mapped to the correct data-URL mime. */
const MARKER_IMAGE_MIME: Record<string, string> = {
  svg: "image/svg+xml",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg"
};

/** Reads a Blob as a base64 `data:` URL, forcing the given mime type. */
const blobToDataUrl = (blob: Blob, mime: string): Promise<string | null> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => resolve(null);
    // Re-wrap with the correct mime — blob storage often serves the wrong
    // Content-Type, and a data: URL must declare the right one to render.
    reader.readAsDataURL(new Blob([blob], { type: mime }));
  });

export const embedMarkerImageUrl = async (url: string): Promise<string> => {
  if (!url || isLocalImageUrl(url)) return url;
  const mime = MARKER_IMAGE_MIME[urlPathExtension(url)];
  if (!mime) return url;
  try {
    const response = await fetch(url);
    if (!response.ok) return url;
    if (mime === "image/svg+xml") {
      // Read as text and force the SVG mime — the response's own Content-Type is
      // the very thing that's wrong, so it must not be trusted here.
      const svg = await response.text();
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
    }
    return (await blobToDataUrl(await response.blob(), mime)) ?? url;
  } catch {
    return url;
  }
};

// Legend Icon Library

/**
 * Icon library for the "Select Icon" marker (ORB-3394) — ids from the equator
 * `Icon` registry (synced to 1.3.0); only the id (`fill_icon_id`) is persisted.
 */
export type LegendIconCategory = {
  id: string;
  label: string;
  icons: readonly string[];
};

// [id, label, space-separated icon ids] tuples — ids as one string (split when
// building the export) so SonarQube's copy-paste detector isn't triggered.
const ICON_CATEGORIES: readonly (readonly [string, string, string])[] = [
  [
    "essential",
    "Essential",
    "account attachment barcode blind bookmark bug camera chat check check-1 circle-check circle-cross circle-plus copy copy-bookmark cross dashboard delete drag-handle edit eyes filter-setting filter-tunnel forbidden form globe help home information integration lightbulb list list-bulleted menu-bento menu-burger minus money money-cash more notif others pause phone play plus scan-barcode search secure setting signout user users warning widget"
  ],
  [
    "arrow",
    "Arrow",
    "arrow-down arrow-left arrow-right arrow-up chev-down chev-left chev-right chev-up circle-arrow-down circle-arrow-left circle-arrow-right circle-arrow-up circle-chev-down circle-chev-left circle-chev-right circle-chev-up circle-triangle-down circle-triangle-left circle-triangle-right circle-triangle-up double-chev-down double-chev-left double-chev-right double-chev-up download graph-down graph-up maximize maximize-2 minimize new-tab open-folder redo refresh small-chev-down small-chev-left small-chev-right small-chev-up sort swap-horizontal swap-vertical triangle-down triangle-left triangle-right triangle-up undo upload"
  ],
  [
    "timeline",
    "Timeline",
    "calendar calendar-1 clock history rewind timer update"
  ],
  [
    "maps",
    "Maps",
    "analysis base-map data-visualization drawing drawing-compass fullscreen layer map-drawing pin poi target"
  ],
  [
    "vehicle",
    "Vehicle",
    "bike bike-1 bike-2 car motorcycle motorcycle-1 plane train walk"
  ],
  ["weather", "Weather", "cloud moon rain sun windy"],
  [
    "chart",
    "Chart",
    "chart-bar-h chart-bar-v chart-big-data chart-line chart-pie"
  ],
  ["miscellaneous", "Miscellaneous", "customer desktop"]
];

export const LEGEND_ICON_CATEGORIES: readonly LegendIconCategory[] =
  ICON_CATEGORIES.map(([id, label, icons]) => ({
    id,
    label,
    icons: icons.split(" ")
  }));

/** Every selectable icon id, in category order (the "All" chip). */
export const ALL_LEGEND_ICONS: readonly string[] =
  LEGEND_ICON_CATEGORIES.flatMap((category) => category.icons);

/** Labels for ids whose auto title-cased form would be unclear. */
const ICON_LABEL_OVERRIDES: Record<string, string> = {
  poi: "POI",
  signout: "Sign Out",
  "check-1": "Check Alt",
  "calendar-1": "Calendar Alt",
  "bike-1": "Bike Alt",
  "bike-2": "Bike Alt 2",
  "motorcycle-1": "Motorcycle Alt",
  "maximize-2": "Maximize Alt",
  "chart-bar-h": "Bar Chart (H)",
  "chart-bar-v": "Bar Chart (V)",
  "chart-big-data": "Big Data Chart"
};

/** Human-readable icon name: "swap-horizontal" → "Swap Horizontal". */
export const getLegendIconLabel = (iconId: string): string =>
  ICON_LABEL_OVERRIDES[iconId] ??
  iconId
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

// 44px bitmap → 22 CSS px on-map (pixelRatio 2), matching the image marker's
// diameter (map.ts SINGLE_STYLE_MARKER_DIAMETER) so icon and image markers render at
// the same size; keep map.ts ICON_MARKER_SELECT_RADIUS in sync.
const MARKER_VIEWBOX = 24;
const MARKER_RENDER_SIZE = 44;
const MARKER_GLYPH_SIZE = 14;
const MARKER_GLYPH_OFFSET = (MARKER_VIEWBOX - MARKER_GLYPH_SIZE) / 2;

/**
 * Colors the equator `Icon` for a standalone SVG data URL (which has no external
 * CSS) by injecting a scoped `<style>` that resolves its Tailwind
 * `fill-current`/`stroke-current` classes to `color`. A CSS rule outranks a
 * hardcoded presentation attribute (e.g. the pin icon's `fill="#616161"`), so
 * the glyph is recolored without editing attributes or risking invalid XML.
 */
const insertIconColorStyle = (svg: string, color: string): string =>
  svg.replace(
    /(<svg\b[^>]*>)/,
    `$1<style>.fill-current{fill:${color}}.stroke-current{stroke:${color}}</style>`
  );

/** Renders an icon id as a white glyph centered inside the marker viewBox. */
const renderIconGlyph = (iconId: string): string => {
  const glyph = renderToStaticMarkup(
    <Icon
      name={iconId}
      width={MARKER_GLYPH_SIZE}
      height={MARKER_GLYPH_SIZE}
      loadableStatus={false}
    />
  );
  // Position the glyph <svg> at the centered offset within the outer viewBox.
  return insertIconColorStyle(glyph, "#FFFFFF").replace(
    "<svg",
    `<svg x="${MARKER_GLYPH_OFFSET}" y="${MARKER_GLYPH_OFFSET}"`
  );
};

// Marker data URLs are pure functions of (iconId, fillColor) — cache them so
// map repaints and legend re-renders don't re-serialize the SVG every time.
const markerUrlCache = new Map<string, string>();

/**
 * Builds a selected icon's marker as an SVG data URL — the row's fill-colored
 * circle with the white glyph — used by both the map and the legend swatch.
 */
export const buildIconMarkerDataUrl = (
  iconId: string,
  fillColor: string
): string => {
  const cacheKey = `${iconId}|${fillColor}`;
  const cached = markerUrlCache.get(cacheKey);
  if (cached) return cached;

  // r + stroke/2 = 12: the white border sits on the bitmap edge, fill runs
  // right up to it — no transparent margin.
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${MARKER_RENDER_SIZE}" ` +
    `height="${MARKER_RENDER_SIZE}" viewBox="0 0 ${MARKER_VIEWBOX} ${MARKER_VIEWBOX}">` +
    `<circle cx="12" cy="12" r="11.4" fill="${fillColor}" stroke="#FFFFFF" stroke-width="1.2"/>` +
    renderIconGlyph(iconId) +
    `</svg>`;
  const url = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  markerUrlCache.set(cacheKey, url);
  return url;
};

/**
 * Resolves a legend row's marker: the icon marker when an icon is selected,
 * else the uploaded image URL, else null (plain colored dot).
 */
export const resolveLegendMarkerImageUrl = (
  row?: {
    iconId?: string | null;
    imageUrl?: string | null;
    fillColor?: string;
  } | null
): string | null => {
  if (!row) return null;
  if (row.iconId) {
    return buildIconMarkerDataUrl(
      row.iconId,
      row.fillColor ?? DEFAULT_SINGLE_STYLE_FILL_COLOR
    );
  }
  return row.imageUrl ?? null;
};

// Custom Legend Config Panels

/** Panel callbacks for a config that previews live, commits on Save, reverts on Cancel and resets. */
export interface ConfigCallbacks<T> {
  onPreview?: (value: T) => void;
  /** Commits the value. Resolving `false` (persist failed) tells the panel to keep
   * Save enabled for a retry. */
  onSave?: (value: T) => void | Promise<boolean | void>;
  onCancel?: () => void;
  onReset?: () => T | null;
  customRangeBounds?: { min?: number; max?: number };
  customRangeHasEmptyValues?: boolean;
}

/** The `useDataset` handlers the legend config panels call, keyed by dataset id. */
export interface CustomLegendActions {
  handlePreviewSingleStyleConfig: (id: string, value: SingleStyleValue) => void;
  handleSaveSingleStyleConfig: (id: string, value: SingleStyleValue) => void;
  handleCancelSingleStyleConfig: (id: string) => void;
  handleResetSingleStyleConfig: (id: string) => SingleStyleValue | null;
  handlePreviewCustomCategoryConfig: (
    id: string,
    value: CustomCategoryValue
  ) => void;
  handleSaveCustomCategoryConfig: (
    id: string,
    value: CustomCategoryValue
  ) => void;
  handleCancelCustomCategoryConfig: (id: string) => void;
  handleResetCustomCategoryConfig: (id: string) => CustomCategoryValue | null;
  handlePreviewCustomRangeConfig: (id: string, value: CustomRangeValue) => void;
  handleSaveCustomRangeConfig: (id: string, value: CustomRangeValue) => void;
  handleCancelCustomRangeConfig: (id: string) => void;
  handleResetCustomRangeConfig: (id: string) => CustomRangeValue | null;
}

/**
 * Builds the Simple / Custom Ordinal / Custom Breaks panel callbacks shared by
 * `DatasetItem` and the Map Preview menu (the Fill and Stroke panels reuse them).
 */
export const buildCustomLegendCallbacks = (
  datasetId: string,
  actions: CustomLegendActions,
  options: {
    resetStepValue: () => void;
    customRangeBounds?: { min?: number; max?: number };
    customRangeHasEmptyValues?: boolean;
    /** Simple-panel Reset is wired only where it's supported (DatasetItem). */
    includeSingleStyleReset?: boolean;
    /** Runs after a Save has committed to the store, to persist it. Resolve `false`
     * when that fails so the panel can offer a retry. */
    onAfterSave?: () => void | Promise<boolean | void>;
  }
): {
  singleStyle: ConfigCallbacks<SingleStyleValue>;
  customCategory: ConfigCallbacks<CustomCategoryValue>;
  customRange: ConfigCallbacks<CustomRangeValue>;
} => {
  const {
    resetStepValue,
    customRangeBounds,
    customRangeHasEmptyValues,
    includeSingleStyleReset = true,
    onAfterSave
  } = options;

  /** Commits the value, then reports the host's persist outcome back to the panel. */
  const withPersist =
    <T,>(save: (value: T) => void) =>
    async (value: T): Promise<boolean> => {
      save(value);
      return (await onAfterSave?.()) ?? true;
    };

  /** Reset writes the default config to the store, so it is persisted too. The
   * panel needs the value back right away, so the save is not awaited. */
  const withResetPersist =
    <T,>(reset: () => T | null) =>
    (): T | null => {
      const value = reset();
      void onAfterSave?.();
      return value;
    };

  return {
    singleStyle: {
      onPreview: (value) =>
        actions.handlePreviewSingleStyleConfig(datasetId, value),
      onSave: withPersist((value: SingleStyleValue) =>
        actions.handleSaveSingleStyleConfig(datasetId, value)
      ),
      onCancel: () => actions.handleCancelSingleStyleConfig(datasetId),
      ...(includeSingleStyleReset && {
        onReset: withResetPersist(() =>
          actions.handleResetSingleStyleConfig(datasetId)
        )
      })
    },
    customCategory: {
      onPreview: (value) =>
        actions.handlePreviewCustomCategoryConfig(datasetId, value),
      onSave: withPersist((value: CustomCategoryValue) =>
        actions.handleSaveCustomCategoryConfig(datasetId, value)
      ),
      onCancel: () => actions.handleCancelCustomCategoryConfig(datasetId),
      onReset: withResetPersist(() => {
        resetStepValue();
        return actions.handleResetCustomCategoryConfig(datasetId);
      })
    },
    customRange: {
      onPreview: (value) =>
        actions.handlePreviewCustomRangeConfig(datasetId, value),
      onSave: withPersist((value: CustomRangeValue) =>
        actions.handleSaveCustomRangeConfig(datasetId, value)
      ),
      onCancel: () => actions.handleCancelCustomRangeConfig(datasetId),
      onReset: withResetPersist(() =>
        actions.handleResetCustomRangeConfig(datasetId)
      ),
      customRangeBounds,
      customRangeHasEmptyValues
    }
  };
};
